import { useEffect, useState } from "react";
import { fetchPolls, votePoll, hasVoted } from "@/lib/api";
import Seo from "@/components/Seo";
import { Check, Vote } from "lucide-react";

export default function Polls() {
    const [polls, setPolls] = useState([]);
    const [voted, setVoted] = useState({});
    const [loading, setLoading] = useState({});

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        const p = await fetchPolls();
        setPolls(p);
        const voteResults = {};
        for (const poll of p) {
            const hv = await hasVoted(poll.id);
            if (hv.has_voted) voteResults[poll.id] = hv.option_id;
        }
        setVoted(voteResults);
    };

    const onVote = async (pollId, optionId) => {
        setLoading((l) => ({ ...l, [pollId]: true }));
        try {
            const updated = await votePoll(pollId, optionId);
            setPolls((ps) => ps.map((p) => (p.id === pollId ? updated : p)));
            setVoted((v) => ({ ...v, [pollId]: optionId }));
        } catch (e) {
            alert(e.response?.data?.detail || "Fejl ved stemmeafgivelse");
        } finally {
            setLoading((l) => ({ ...l, [pollId]: false }));
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="polls-page">
            <Seo
                title="Afstemninger — hvad mener du?"
                description="Anonyme afstemninger om politisk tillid, valgløfter og danske politikere. Din stemme tæller direkte i statistikken."
                keywords="afstemninger, meningsmåling, politisk tillid, danskernes holdning, anonym stemme"
                path="/afstemninger"
            />
            <div className="mb-12">
                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                    Afstemninger
                </span>
                <h1 className="font-serif-dm text-5xl text-royal mt-2">
                    Din stemme tæller
                </h1>
                <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                    Tag pulsen på den politiske tillid i Danmark. Dine stemmer er
                    anonyme og tæller direkte i det samlede billede.
                </p>
            </div>

            <div className="space-y-8">
                {polls.map((poll) => {
                    const userVotedOption = voted[poll.id];
                    const totalVotes = poll.total_votes || 0;
                    return (
                        <div
                            key={poll.id}
                            className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8"
                            data-testid={`poll-${poll.id}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Vote className="w-4 h-4 text-royal" />
                                <span className="text-xs uppercase tracking-wider text-[#4F5A7A] font-semibold">
                                    {totalVotes} stemmer
                                </span>
                            </div>
                            <h2 className="font-serif-dm text-2xl md:text-3xl text-royal leading-tight mb-2">
                                {poll.question}
                            </h2>
                            {poll.description && (
                                <p className="text-sm text-[#4F5A7A] mb-6">{poll.description}</p>
                            )}

                            <div className="space-y-2.5">
                                {poll.options.map((opt) => {
                                    const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                                    const isUserChoice = userVotedOption === opt.id;
                                    const canVote = !userVotedOption;
                                    return (
                                        <button
                                            key={opt.id}
                                            disabled={!canVote || loading[poll.id]}
                                            onClick={() => onVote(poll.id, opt.id)}
                                            data-testid={`poll-option-${opt.id}`}
                                            className={`w-full text-left relative overflow-hidden rounded-xl border-2 transition-all ${
                                                canVote
                                                    ? "border-[#E5E8F0] hover:border-royal cursor-pointer"
                                                    : isUserChoice
                                                    ? "border-royal bg-royal-soft"
                                                    : "border-[#E5E8F0] cursor-default"
                                            }`}
                                        >
                                            {!canVote && (
                                                <div
                                                    className="absolute inset-y-0 left-0 bg-royal-soft"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            )}
                                            <div className="relative flex items-center justify-between px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {isUserChoice && (
                                                        <Check className="w-4 h-4 text-royal" />
                                                    )}
                                                    <span
                                                        className={`font-medium ${
                                                            isUserChoice ? "text-royal" : "text-[#0B1A3A]"
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </span>
                                                </div>
                                                {!canVote && (
                                                    <div className="text-sm text-[#4F5A7A] font-semibold">
                                                        {pct}% · {opt.votes}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {userVotedOption && (
                                <p className="text-xs text-[#4F5A7A] mt-4 italic" data-testid={`voted-msg-${poll.id}`}>
                                    Du har afgivet din stemme — tak for din deltagelse.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
